import { Inject, Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AuthStore, InstallationDetailsStore, RemoteConfigStore } from '@store';
import { EnvironmentService } from '../environment.service';
import { viguideVersion } from '../../../../../package-version';

@Injectable({
  providedIn: 'root',
})
export class AppInsightsMonitoringService {
  appInsights: ApplicationInsights;
  uuid: string;
  gatewaySerial: string;
  remoteConfigUserLogLevel = 0;
  // propery timestamp only used for unit tests to mock time
  timestamp: string;

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public authStore: AuthStore,
    public remoteConfigStore: RemoteConfigStore,
    @Inject(ApplicationInsights) ApplicationInsightsService: typeof ApplicationInsights
  ) {
    const appInsightsConfig = EnvironmentService.getVariable<{
      config: {
        connectionString: string;
        enableAutoRouteTracking: boolean;
      };
    }>('appInsights').config;

    this.authStore.userId$.subscribe((id) => {
      this.uuid = id;
    });

    this.installationDetailsStore.getSelectedGateway$.subscribe((gateway) => {
      this.gatewaySerial = gateway?.serial;
    });

    this.remoteConfigStore.appInsightsUserLogLevel$.subscribe((level) => {
      this.remoteConfigUserLogLevel = level;
    });

    if (appInsightsConfig.connectionString) {
      this.appInsights = new ApplicationInsightsService({
        config: {
          connectionString: appInsightsConfig.connectionString,
          enableAutoRouteTracking: appInsightsConfig.enableAutoRouteTracking,
        },
      });
      this.appInsights.loadAppInsights();
    }
  }

  logEvent(name: string, logLevel: LogLevelEnum, properties?: { [key: string]: any }): void {
    if (!this.shouldLog(logLevel)) {
      return;
    }

    let timestamp: string;
    if (this.timestamp) {
      timestamp = this.timestamp;
    } else {
      timestamp = new Date().toISOString();
    }

    if (properties) {
      properties.uuid = this.uuid;
      properties.gatewaySerial = this.gatewaySerial;
      properties.timestamp = timestamp;
    } else {
      properties = {
        uuid: this.uuid,
        gatewaySerial: this.gatewaySerial,
        timestamp,
      };
    }

    if (viguideVersion) {
      properties.version = viguideVersion;
    }

    this.appInsights.trackEvent({ name }, properties);
  }

  // uncomment to use when needed:

  // logPageView(name?: string, url?: string) {
  //   this.appInsights.trackPageView({
  //     name,
  //     uri: url,
  //   });
  // }

  // logMetric(name: string, average: number, properties?: { [key: string]: string }) {
  //   this.appInsights.trackMetric({ name, average }, properties);
  // }

  // logException(exception: Error, severityLevel?: number) {
  //   this.appInsights.trackException({ exception, severityLevel });
  // }

  // logTrace(message: string, properties?: { [key: string]: string }) {
  //   this.appInsights.trackTrace({ message }, properties);
  // }

  private shouldLog(logLevel: LogLevelEnum): boolean {
    if (!this.appInsights || !this.remoteConfigUserLogLevel) {
      return false;
    }

    return this.remoteConfigUserLogLevel >= logLevel;
  }
}

export const APP_INSIGHTS_LIVE_UPDATES = {
  // [LU] = Live Updates
  INVOKE_CALL_CREATE_SUBSCRIPTION_ATTEMPT: '[LU] INVOKE CALL TO CREATE SUBSCRIPTION ATTEMPT',
  INVOKE_CALL_CREATE_SUBSCRIPTION_SUCCESS: '[LU] INVOKE CALL TO CREATE SUBSCRIPTION SUCCESS',
  INVOKE_CALL_CREATE_SUBSCRIPTION_FAILED: '[LU] INVOKE CALL TO CREATE SUBSCRIPTION FAILED',

  RECONNECT: '[LU] RECONNECT',
  ON_UPDATE_QUERY: '[LU] ON UPDATE QUERY',
  ON_ERROR: '[LU] LIVE UPDATE ERROR',
  ON_NETWORK_ONLINE: '[LU] NETWORK ONLINE',
};

export enum LogLevelEnum {
  ///////////////////////////////////////////////////////////////////////////////////
  // we log all log entries with the specified log level and below, for example:
  // if enabled log level is 3 (trace) then we will log all trace logs + all debug logs + all error logs
  // if enabled log level is 1 (error) then we will log only error logs
  // when log level is set to 0 then logging is turned off
  //
  // log level is defined in: remoteConfig.appInsightsUserLogLevel
  ///////////////////////////////////////////////////////////////////////////////////
  ERROR = 1,
  DEBUG = 2,
  TRACE = 3,
}
